import { useMemo } from 'react';

import { PATIENT_SNAPSHOT_TAB_ANCHOR } from '@/app/hints';
import { useCareStateDomain } from '@/entities/care-state-domain';
import { toPatientProfile, useUserInfoQuery } from '@/entities/user-info';
import { useHintAnchor } from '@/shared/ui/primitives';

import { PatientNoteView } from './PatientNoteView';
import { PatientProfilePanel } from './PatientProfilePanel';

interface PatientSnapshotTabProps {
  userId: string;
}

export function PatientSnapshotTab({ userId }: PatientSnapshotTabProps) {
  const userInfoQuery = useUserInfoQuery(userId);
  const { careState } = useCareStateDomain();
  const snapshotAnchorRef = useHintAnchor(PATIENT_SNAPSHOT_TAB_ANCHOR);

  const profile = useMemo(() => {
    const info = userInfoQuery.data?.patientInfo;
    if (!info) {
      return null;
    }
    return toPatientProfile(info);
  }, [userInfoQuery.data]);

  const patientNote = useMemo(() => {
    if (careState === null) {
      return '';
    }
    const patient = careState.patients.find((p) => p.id === 'root');
    return patient?.note.value ?? '';
  }, [careState]);

  if (userInfoQuery.isLoading) {
    return <div className="p-3 text-sm text-muted-foreground">Loading snapshot…</div>;
  }

  if (userInfoQuery.isError || profile === null) {
    return <div className="p-3 text-sm text-muted-foreground">Could not load snapshot.</div>;
  }

  return (
    <div ref={snapshotAnchorRef} className="h-full overflow-auto p-3">
      <div className="grid grid-cols-2 gap-2 pr-3">
        <PatientProfilePanel profile={profile} />

        <section
          aria-label="Summary"
          className="flex flex-col gap-3 rounded-[12px] bg-surface-sunken p-3"
        >
          <h2 className="text-lg font-medium tracking-tight text-foreground">Summary</h2>
          <PatientNoteView note={patientNote} noteId={`patient-note:${userId}`} />
        </section>
      </div>
    </div>
  );
}
